import React, { useRef, useState } from 'react'
import { Box, TextField, IconButton, InputAdornment, Tooltip, Divider, CircularProgress, FormHelperText } from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import useVoiceDictation from '../../../../hooks/useVoiceDictation'
import useDictationAccess from '../../../../hooks/useDictationAccess'
import useAccount from '../../../../hooks/useAccount'
import DisabledReason from '../../../system/DisabledReason'
import { MentionPicker } from './MentionPicker'
import { GuidedToggles } from './GuidedToggles'
import {
  GuidedToggleState,
  PersistedGuidedToggles,
  detectMentionQuery,
  insertMention,
  filterMentionSources,
  applyGuidedToggles,
  deriveGuidedToggles,
} from './wizardHelpers'

export interface InstructionsFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  mentionSources: string[]
  rows?: number
  helperText?: React.ReactNode
  /** Hide the guided-question toggles (bullets/prose, length, audience) — the
   * synthesis stage of the flat editor doesn't need them, only Step 2 / the
   * analysis stage do. Defaults to shown. */
  showGuidedToggles?: boolean
  /** Structured guided-toggle state persisted alongside `value` (#1047) — the
   * source of truth for what the toggle buttons show on (re)mount. Undefined
   * for widgets saved before these fields existed; the toggle state shown is
   * then best-effort parsed from `value`'s trailing guidance sentence instead
   * (see `deriveGuidedToggles`). Omit entirely for callers that don't persist
   * toggle state — the toggles still work, they just won't survive a reopen. */
  guidedToggles?: GuidedToggleState | PersistedGuidedToggles
  onGuidedTogglesChange?: (next: GuidedToggleState) => void
}

/**
 * Shared instructions textarea for the Gen AI Insights wizard (#970 Part A,
 * Step 2) and the flat re-edit modal (Part B). Combines:
 *  - free-form text editing
 *  - an "@" mention picker over the sources selected in Step 1, inserting an
 *    inline `[@Label]` token at the cursor
 *  - guided toggles that auto-inject a trailing guidance sentence
 *  - voice input via the existing assistant transcription path
 *    (useVoiceDictation → POST /transcribe), appending to the field (#1048)
 */
export const InstructionsField: React.FC<InstructionsFieldProps> = ({
  label,
  value,
  onChange,
  mentionSources,
  rows = 6,
  helperText,
  showGuidedToggles = true,
  guidedToggles,
  onGuidedTogglesChange,
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const [mention, setMention] = useState<{ start: number; end: number; query: string } | null>(null)
  // Derived in render, not mirrored into state (#1047): the toggle buttons
  // always reflect the persisted structured fields (or a best-effort parse of
  // the trailing guidance sentence when there are none) rather than an
  // internal `useState({})` that only ever reflected the last click made
  // during THIS mount — which showed defaults every time the field reopened
  // with existing data.
  // Guard against a caller that hasn't wired `value` yet (a stale/incomplete
  // prop set, e.g. mid-refactor test fixtures) — `value` is typed as
  // `string` but nothing enforces that at the JS runtime boundary.
  const toggles = deriveGuidedToggles(value ?? '', guidedToggles)

  // Voice input (#970: "Microphone available on the instructions field" —
  // wired under #1048 via the existing assistant transcription path:
  // record → POST /transcribe → append). `value` is read through a ref at
  // transcription time, so text typed while the recording was in flight is
  // never clobbered by the stale closure captured at record-start.
  const valueRef = useRef(value)
  valueRef.current = value
  const voice = useVoiceDictation({
    onTranscription: (text) => {
      const current = valueRef.current
      onChange(current.trim() ? `${current.trimEnd()} ${text}` : text)
    },
  })

  // Dictation needs agent access and budget (audit C23); Help me write this
  // opens below it, so the mic says why it is off.
  const dictation = useDictationAccess(useAccount().customer?.name)
  const micButton = (
    <IconButton
      size="small"
      aria-label={voice.isRecording ? 'Stop recording' : 'Voice input'}
      onClick={() => (voice.isRecording ? voice.stopRecording() : voice.startRecording())}
      disabled={voice.isTranscribing}
      sx={{ color: voice.isRecording ? 'error.main' : undefined }}
    >
      {voice.isTranscribing
        ? <CircularProgress size={16} />
        : voice.isRecording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
    </IconButton>
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    const cursor = e.target.selectionStart ?? newValue.length
    const match = detectMentionQuery(newValue, cursor)
    setMention(match)
  }

  const handleMentionSelect = (sourceLabel: string) => {
    if (!mention) return
    const { text, cursorPos } = insertMention(value, mention.start, mention.end, sourceLabel)
    onChange(text)
    setMention(null)
    // Restore focus + cursor after the inserted token.
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(cursorPos, cursorPos)
    })
  }

  const handleTogglesChange = (next: GuidedToggleState) => {
    onGuidedTogglesChange?.(next)
    onChange(applyGuidedToggles(value, next))
  }

  const filteredSources = filterMentionSources(mentionSources, mention?.query ?? '')

  return (
    <Box>
      <TextField
        fullWidth
        multiline
        rows={rows}
        size="small"
        label={label}
        value={value}
        onChange={handleChange}
        onBlur={() => {
          // Delay so a click on the mention list isn't lost to the blur.
          setTimeout(() => setMention(m => (document.activeElement === inputRef.current ? m : null)), 150)
        }}
        inputRef={inputRef}
        helperText={helperText}
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              {dictation.reason ? (
                <DisabledReason reason={dictation.reason}>{micButton}</DisabledReason>
              ) : (
                <Tooltip title={voice.isRecording ? 'Stop recording' : 'Voice input'}>
                  <span>{micButton}</span>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
      />
      {voice.error && <FormHelperText error>{voice.error}</FormHelperText>}
      <MentionPicker
        anchorEl={inputRef.current}
        open={Boolean(mention)}
        sources={filteredSources}
        onSelect={handleMentionSelect}
        onClose={() => setMention(null)}
      />

      {showGuidedToggles && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <GuidedToggles value={toggles} onChange={handleTogglesChange} />
        </>
      )}
    </Box>
  )
}

export default InstructionsField
